/**
 * In-context permission helpers (iter53).
 *
 * Play Store + App Store policy: any permission whose use is not
 * essential to first-launch onboarding must be requested at the
 * moment the user performs the action that needs it. We honour
 * that by routing camera / media-library prompts through this
 * module instead of the onboarding screen.
 *
 * Each helper returns one of:
 *   "granted" | "denied" | "prompt" | "unsupported" | "error"
 *
 * They are deliberately tolerant — a denied or unsupported state
 * does not crash the caller; the caller falls through to the
 * normal file-picker, which lets the user pick from gallery even
 * when the camera is blocked.
 */
import { Capacitor } from "@capacitor/core";

const CAM_PROMPTED_KEY = "bizil_camera_prompted";

/** Lightweight permission probe using the standard Permissions API
 *  (Chromium-based WebViews on Android 13+ and Safari WebView on iOS
 *  17+ implement it). Falls back to "prompt" when unavailable. */
async function _queryCameraState() {
    if (!navigator.permissions?.query) return "prompt";
    try {
        const status = await navigator.permissions.query({ name: "camera" });
        return status.state;             // "granted" | "denied" | "prompt"
    } catch {
        return "prompt";
    }
}

/**
 * Trigger the OS camera-permission prompt **once**, in-context.
 *
 *   - On the first call we open a short-lived ``getUserMedia`` stream
 *     just long enough for the OS to surface its native permission
 *     dialog, then immediately release the device. We do NOT keep the
 *     stream alive — that would steal the camera from the file-picker
 *     intent that runs right after.
 *   - On subsequent calls we re-use the cached state (cheap probe via
 *     the Permissions API) so we don't repeatedly nag the user.
 *   - We deliberately fail-open: an "error" / "unsupported" result
 *     still lets the caller proceed with the regular file picker
 *     (where the user can pick from gallery without the camera
 *     permission at all).
 *
 *   @returns {Promise<"granted"|"denied"|"prompt"|"unsupported"|"error">}
 */
export async function requestCameraPermission() {
    // Probe first — if the OS has already granted/denied, no prompt.
    const initial = await _queryCameraState();
    if (initial === "granted" || initial === "denied") return initial;

    // We only show the OS prompt if we have a way to release the stream
    // (otherwise the request blocks the file-picker that follows).
    if (!navigator.mediaDevices?.getUserMedia) return "unsupported";

    try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach((t) => t.stop());
        try { localStorage.setItem(CAM_PROMPTED_KEY, "1"); } catch { /* ignore */ }
        return "granted";
    } catch (e) {
        if (e?.name === "NotAllowedError") return "denied";
        if (e?.name === "NotFoundError")   return "unsupported";
        return "error";
    }
}

/** True once the camera prompt has been surfaced at least once on
 *  this device, so callers can avoid re-prompting on every click. */
export function hasPromptedForCamera() {
    try { return !!localStorage.getItem(CAM_PROMPTED_KEY); }
    catch { return false; }
}

/** Convenience flag — only relevant inside a Capacitor WebView; on
 *  pure-web the OS picker handles its own permission UX, so callers
 *  should skip the in-context warm-up there. */
export function isNativeApp() {
    try { return Capacitor.isNativePlatform(); }
    catch { return false; }
}
